// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * OAuth redirect target for social sign-in (T18 follow-up).
 *
 * The Cognito domain redirects here with ?code&state after Google/Apple
 * authentication. The page validates the state, exchanges the code (PKCE),
 * and hands the token triple to the SAME session layer as a password login
 * (AuthProvider.applySession → bb_session_v1, ADR-020) — so cross-tab sync,
 * scheduled refresh, and reload restore behave identically.
 *
 * Routing after success is intentionally just "/app": RequireAuth +
 * RequireOnboardingComplete already send a first-time federated user (whose
 * `users` row is auto-provisioned by the /v1/me bootstrap, exactly like an
 * email signup) into onboarding, and returning users to Today.
 */

import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useApiClient } from "@/api/useApiClient"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import { cognitoErrorMessageKey } from "@/lib/cognito/errors"
import { recordPendingConsent } from "@/lib/consent"
import { trackFunnelEvent } from "@/lib/analytics"

/** An account whose `/v1/me` row is younger than this just signed up (the server creates it on the first /v1/me). */
const FRESH_ACCOUNT_MS = 15 * 60_000
/** Never hold the visitor on the callback screen for analytics. */
const ME_LOOKUP_TIMEOUT_MS = 4_000

export default function AuthCallbackPage() {
  const { completeSocialSignIn, email } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const api = useApiClient()
  const [failedKey, setFailedKey] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const ranRef = useRef(false)

  useEffect(() => {
    // The exchange must run exactly once — React StrictMode double-invokes
    // effects, and an authorization code is single-use.
    if (ranRef.current) return
    ranRef.current = true

    const params = new URLSearchParams(window.location.search)
    completeSocialSignIn(params)
      .then(async () => {
        // The single-use `code` and `state` are spent; take them out of the address bar before any
        // analytics call can read the URL.
        try {
          window.history.replaceState(window.history.state, "", window.location.pathname)
        } catch {
          /* best-effort */
        }
        await trackSocialSignup()
        setDone(true)
      })
      .catch(error => setFailedKey(cognitoErrorMessageKey(error)))
    // `api` and `completeSocialSignIn` are stable; the exactly-once guard above is what matters.
  }, [completeSocialSignIn])

  /**
   * Social sign-in and social sign-up are the same redirect, so the only way to tell a brand-new account is its
   * age: the server creates the `users` row on the first /v1/me, so a just-created federated user has a fresh
   * `createdAt`. A failed or slow lookup fires nothing and never blocks the navigation.
   */
  async function trackSocialSignup() {
    try {
      const me = await Promise.race([
        api.getMe(),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), ME_LOOKUP_TIMEOUT_MS)),
      ])
      const createdAt = Date.parse(me.createdAt)
      if (Number.isFinite(createdAt) && Date.now() - createdAt < FRESH_ACCOUNT_MS) {
        trackFunnelEvent("signup_completed", { method: "social" })
      }
    } catch {
      // not tracked, not blocking
    }
  }

  useEffect(() => {
    if (!done) return
    // The social buttons carry the "by continuing you agree…" notice; keep
    // the same local pending-consent record an email signup writes (the
    // server records consent_records at /v1/me bootstrap either way).
    if (email) recordPendingConsent(email)
    navigate("/app", { replace: true })
  }, [done, email, navigate])

  return (
    <main className="flex min-h-screen items-center justify-center bg-cream px-4">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        {failedKey ? (
          <>
            <p role="alert" className="text-sm text-destructive">
              {t(failedKey)}
            </p>
            <button
              type="button"
              className="min-h-11 font-medium text-green underline underline-offset-2"
              onClick={() => navigate("/", { replace: true })}
            >
              {t("auth.backToSignIn")}
            </button>
          </>
        ) : (
          <p role="status" className="text-sm text-ink">
            {t("auth.socialCompleting")}
          </p>
        )}
      </div>
    </main>
  )
}
